import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { shouldDismissOverlay } from './ui-behavior';
import { getVoicePreviewUrl } from './api';
import { GEMINI_VOICES, voiceGenderLabel, type GeminiVoiceId } from '../shared/voices';

type VoiceLibraryModalProps = {
  speakerName: string;
  selectedVoice: GeminiVoiceId;
  onClose: () => void;
  onSelect: (voice: GeminiVoiceId) => Promise<void> | void;
};

function VoicePreviewAudio({ voice, className, onError }: { voice: GeminiVoiceId; className: string; onError: () => void }) {
  const [loading, setLoading] = useState(true);
  return <span className="voice-preview-audio-wrap">
    {loading && <small className="voice-preview-loading" role="status">首次试听可能需要十几秒，正在准备…</small>}
    <audio className={className} autoPlay controls preload="auto" src={getVoicePreviewUrl(voice)} onCanPlay={() => setLoading(false)} onPlaying={() => setLoading(false)} onError={() => { setLoading(false); onError(); }} />
  </span>;
}

export function VoicePreviewButton({ voice }: { voice: GeminiVoiceId }) {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');

  return <span className="voice-inline-preview">
    <button className="voice-inline-preview-button" type="button" onClick={() => { setError(''); setPlaying((current) => !current); }}>{playing ? '关闭试听' : '试听'}</button>
    {playing && <VoicePreviewAudio className="voice-inline-preview-player" voice={voice} onError={() => setError('试听失败，请稍后重试。')} />}
    {error && <small className="voice-inline-preview-error" role="alert">{error}</small>}
  </span>;
}

function useVoiceModalLock() {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);
}

export function VoiceLibraryModal({ speakerName, selectedVoice, onClose, onSelect }: VoiceLibraryModalProps) {
  const [previewVoice, setPreviewVoice] = useState<string | null>(null);
  const [savingVoice, setSavingVoice] = useState<string | null>(null);
  const [error, setError] = useState('');
  useVoiceModalLock();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !savingVoice) onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, savingVoice]);

  async function chooseVoice(voice: string) {
    setSavingVoice(voice);
    setError('');
    try {
      await onSelect(voice as GeminiVoiceId);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '音色保存失败，请重试。');
    } finally {
      setSavingVoice(null);
    }
  }

  return createPortal(
    <div className="modal-backdrop voice-library-backdrop" role="presentation" onMouseDown={(event) => { if (shouldDismissOverlay(event.target, event.currentTarget, Boolean(savingVoice))) onClose(); }}>
      <section className="continuation-modal voice-library-modal" role="dialog" aria-modal="true" aria-labelledby="voice-library-title">
        <div className="modal-heading voice-library-heading"><div><span className="section-kicker">Gemini 音色库</span><h2 id="voice-library-title">为{speakerName}<br /><em>选一副声音。</em></h2></div><button aria-label="关闭音色选择" className="modal-close" disabled={Boolean(savingVoice)} type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">试听采用统一中文短句，不会读取正文。首次试听需要实时生成，之后会从云端缓存直接加载。</p>
        {error && <p className="voice-library-error" role="alert">{error}</p>}
        <div className="voice-library-list">
          {GEMINI_VOICES.map((voice) => {
            const selected = voice.id === selectedVoice;
            const previewing = previewVoice === voice.id;
            return <div className={`voice-library-row${selected ? ' is-selected' : ''}`} key={voice.id}>
              <div className="voice-library-copy"><strong>{voice.id}</strong><span className={`voice-gender-badge is-${voice.gender}`}>{voiceGenderLabel(voice.id)}</span><span>{voice.style}</span><small>{voice.description}</small>{previewing && <VoicePreviewAudio className="voice-preview-player" voice={voice.id} onError={() => setError('试听音频生成失败，请稍后重试。')} />}</div>
              <div className="voice-library-actions"><button className="voice-preview-button" type="button" onClick={() => setPreviewVoice(previewing ? null : voice.id)}>{previewing ? '关闭试听' : '▶ 试听'}</button>{selected ? <span className="voice-current-mark">当前</span> : <button className="voice-select-button" disabled={Boolean(savingVoice)} type="button" onClick={() => void chooseVoice(voice.id)}>{savingVoice === voice.id ? '保存中…' : '选择'}</button>}</div>
            </div>;
          })}
        </div>
        <p className="voice-library-footnote">30 个音色的男女性别依据 Google 官方目录标记。试听生成后会永久缓存，重复试听不会再次调用模型。</p>
      </section>
    </div>,
    document.body,
  );
}
